/**
 * Crypto utilities for offline credential storage
 * Uses Web Crypto API for secure encryption
 */

// Derive encryption key from password using PBKDF2
const deriveKey = async (password, salt) => {
  const encoder = new TextEncoder();
  const passwordKey = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(salt),
      iterations: 100000,
      hash: 'SHA-256'
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

// Generate a random salt
const generateSalt = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
};

// Generate a random IV for AES-GCM
const generateIV = () => {
  return window.crypto.getRandomValues(new Uint8Array(12));
};

// Create a password verifier (hash that can be used to verify password offline)
export const createPasswordVerifier = async (email, password) => {
  const salt = generateSalt();
  const key = await deriveKey(password, salt + email);
  
  // Create a known plaintext to encrypt - this will serve as our verifier
  const verifierPlaintext = `ABBEYZONE_VERIFIER_${email}`;
  const encoder = new TextEncoder();
  const iv = generateIV();
  
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(verifierPlaintext)
  );

  return {
    salt,
    iv: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
    verifier: Array.from(new Uint8Array(encrypted))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  };
};

// Verify password against stored verifier
export const verifyPassword = async (email, password, storedCredential) => {
  try {
    const { salt, iv, verifier } = storedCredential;
    const key = await deriveKey(password, salt + email);
    
    // Convert hex strings back to arrays
    const ivArray = new Uint8Array(
      iv.match(/.{2}/g).map(hex => parseInt(hex, 16))
    );
    const encryptedArray = new Uint8Array(
      verifier.match(/.{2}/g).map(hex => parseInt(hex, 16))
    );
    
    // Try to decrypt - if successful, password is correct
    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivArray },
      key,
      encryptedArray
    );
    
    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decrypted);
    
    // Verify the decrypted content matches expected pattern
    return decryptedText === `ABBEYZONE_VERIFIER_${email}`;
  } catch (error) {
    // Decryption failed - wrong password
    return false;
  }
};

// Encrypt user data for offline storage
export const encryptUserData = async (password, salt, userData) => {
  const key = await deriveKey(password, salt);
  const encoder = new TextEncoder();
  const iv = generateIV();
  
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(JSON.stringify(userData))
  );

  return {
    iv: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
    data: Array.from(new Uint8Array(encrypted))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  };
};

// Decrypt user data
export const decryptUserData = async (password, salt, encryptedData) => {
  try {
    const { iv, data } = encryptedData;
    const key = await deriveKey(password, salt);
    
    const ivArray = new Uint8Array(
      iv.match(/.{2}/g).map(hex => parseInt(hex, 16))
    );
    const dataArray = new Uint8Array(
      data.match(/.{2}/g).map(hex => parseInt(hex, 16))
    );
    
    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivArray },
      key,
      dataArray
    );
    
    const decoder = new TextDecoder();
    return JSON.parse(decoder.decode(decrypted));
  } catch (error) {
    console.error('Failed to decrypt user data:', error);
    return null;
  }
};

export const crypto = {
  createPasswordVerifier,
  verifyPassword,
  encryptUserData,
  decryptUserData,
  generateSalt
};

export default crypto;
